import React from 'react';
import { CmsBestForBrowsePage } from 'js/model/cms/cms-best-for-browse-page';
import {
  createHeaderViewModel,
  Header,
} from 'js/pages/BestForBrowsePage/Header';
import {
  createVenueViewModel,
  Venue,
} from 'js/pages/BestForBrowsePage/Venue/Venue';
import {
  createRatingWithReviewsViewModel,
  RatingWithReviews,
} from 'js/pages/BestForBrowsePage/RatingWithReviews';
import { ButtonColour, ButtonSize } from 'js/components/Button';
import { TreatmentGuideIcon } from 'js/components/Icons/TreatmentGuideIcon/TreatmentGuideIcon';
import { ButtonAnchor } from 'js/components/ButtonAnchor/ButtonAnchor';
import { PropositionSlots } from 'js/pages/BestForBrowsePage/PropositionSlots/PropositionSlots';
import { CrossLinks } from 'js/pages/BestForBrowsePage/CrossLinks/CrossLinks';
import { createPropositionSlotsViewModel } from 'js/model/view/proposition-slots/proposition-slots';
import {
  trackBrowsePageClick,
  trackBttPageClick,
  trackGoToVenueClick,
  trackScrollDepth,
  trackTreatmentGuideClick,
} from 'js/pages/BestForBrowsePage/tracking';
import { substitute, substituteWithDictionary } from 'js/helpers/i18n';
import { triggerOnScrollDepths } from 'js/helpers/scroll-depth';
import { IndexDictionary } from 'js/types/generic-types';
import { BestForBrowseVenueData } from 'js/model/rainbow/best-for-browse-page/BestForBrowsePageVenueData';
import { Context } from 'js/components/LocaleWrapper';
import { useTrackPageAndSessionView } from 'js/hooks/useTrackPageAndSessionView';
import styles from './BestForBrowsePage.module.css';

export function BestForBrowsePage(): React.ReactElement {
  const context = React.useContext(Context);

  React.useEffect(() => {
    const venueCount: number =
      context.pageData.bestForBrowse.data.venues.length;
    const triggers: IndexDictionary<() => void> = {
      10: () => trackScrollDepth(10, venueCount),
      25: () => trackScrollDepth(25, venueCount),
      50: () => trackScrollDepth(50, venueCount),
      75: () => trackScrollDepth(75, venueCount),
      90: () => trackScrollDepth(90, venueCount),
      100: () => trackScrollDepth(100, venueCount),
    };
    triggerOnScrollDepths(triggers);
  }, []);
  useTrackPageAndSessionView();

  function renderTreatmentGuideIcon(): React.ReactElement {
    return <TreatmentGuideIcon />;
  }

  function renderVenues(
    venues: BestForBrowseVenueData[],
    cms: CmsBestForBrowsePage
  ): React.ReactNode[] {
    const venueCms = cms.page['best-for-browse'].venue;
    return venues.map((venue, index) => (
      <Venue
        key={venue.normalisedName}
        {...createVenueViewModel(
          venue,
          venueCms,
          index + 1,
          context.generateUri,
          trackGoToVenueClick,
          context.channel.country.countryCode
        )}
      >
        <RatingWithReviews
          {...createRatingWithReviewsViewModel(
            venue,
            venueCms,
            context.generateUri
          )}
        />
      </Venue>
    ));
  }

  const pageOutput = context.pageData.bestForBrowse;
  const cms = context.pageData.cms;
  const pageParameters = context.pageData.pageParameters as unknown as {
    treatment: [string];
    location: [string];
  };

  const treatmentNormalisedName = pageParameters.treatment[0];
  const locationNormalisedName = pageParameters.location[0];

  const inspirationLinksCms = cms.page['best-for-browse']['inspiration-links'];

  const propositionSlotsCms = cms['proposition-slots'];
  const propositionSlotsComponent: React.ReactElement = (
    <PropositionSlots
      {...createPropositionSlotsViewModel(propositionSlotsCms)}
    />
  );

  const alternativePagesHeadingCms =
    cms.page['best-for-browse']['cross-links'].heading;
  const alternativePagesBadgeCms =
    cms.page['best-for-browse']['cross-links']['badge-template'];
  const alternativePages = pageOutput.data.alternativePages.map((page) => ({
    ...page,
    onClick: (event: React.SyntheticEvent<HTMLAnchorElement>) =>
      trackBttPageClick(event, page.uri, page.treatmentName, page.treatmentId),
  }));

  const venues = renderVenues(pageOutput.data.venues, cms);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Header
          {...createHeaderViewModel(
            pageOutput,
            cms.page['best-for-browse'].header,
            cms.common
          )}
        />
      </div>
      <div className={styles.pageWidthContainer}>
        <div className={styles.listColumn}>
          <div className={styles.venues}>
            {[
              ...venues.slice(0, 3),
              <div
                key="propositionSlots"
                className={styles.propositionSlotsForMobile}
              >
                {propositionSlotsComponent}
              </div>,
              ...venues.slice(3),
            ]}
          </div>
          {alternativePages.length !== 0 && (
            <div className={styles.crossLinks}>
              <CrossLinks
                heading={alternativePagesHeadingCms}
                badge={substitute(
                  alternativePagesBadgeCms,
                  pageOutput.data.treatment
                )}
                treatments={alternativePages}
              />
            </div>
          )}
        </div>
        <div className={styles.rightColumn}>
          <div className={styles.propositionSlotsForTabletAndDesktop}>
            {propositionSlotsComponent}
          </div>
          <div className={styles.getTheFacts}>
            <ButtonAnchor
              {...createGetTheFactsLinkViewModel(
                pageOutput.data.treatment,
                treatmentNormalisedName,
                inspirationLinksCms,
                context.generateUri,
                ButtonColour.BlueOutline,
                ButtonSize.Medium,
                trackTreatmentGuideClick,
                pageOutput.data.treatmentId,
                renderTreatmentGuideIcon
              )}
            />
          </div>
          <a
            className={styles.seeAllLink}
            {...createSeeAllLinkViewModel(
              pageOutput.data.treatment,
              treatmentNormalisedName,
              locationNormalisedName,
              inspirationLinksCms,
              context.generateUri,
              pageOutput.data.treatmentId,
              trackBrowsePageClick
            )}
          />
        </div>
      </div>
    </div>
  );
}

type GenerateSeeAllUriFunction = (
  pageType: string,
  values: { location: [string]; treatments: [string] }
) => string;

export function createSeeAllLinkViewModel(
  treatmentName: string,
  treatmentNormalisedName: string,
  locationNormalisedName: string,
  cms: { 'see-all-link-template': string },
  generateUriFunction: GenerateSeeAllUriFunction,
  treatmentId: number,
  trackingFunction: (
    event: React.SyntheticEvent<HTMLAnchorElement>,
    treatmentName: string,
    treatmentId: number
  ) => void
): {
  href: string;
  children: string;
  onClick: (event: React.SyntheticEvent<HTMLAnchorElement>) => void;
} {
  return {
    children: substituteWithDictionary(cms['see-all-link-template'], {
      treatment: treatmentName,
    }),
    href: generateUriFunction('browse', {
      location: [locationNormalisedName],
      treatments: [treatmentNormalisedName],
    }),
    onClick: (event: React.SyntheticEvent<HTMLAnchorElement>) =>
      trackingFunction(event, treatmentName, treatmentId),
  };
}

type GenerateGetTheFactsUriFunction = (
  pageType: string,
  values: { normalisedName: [string] }
) => string;

export function createGetTheFactsLinkViewModel(
  treatmentName: string,
  treatmentNormalisedName: string,
  cms: { 'get-the-facts-link-template': string },
  generateUriFunction: GenerateGetTheFactsUriFunction,
  colour: ButtonColour,
  size: ButtonSize,
  trackingFunction: (
    event: React.SyntheticEvent<HTMLAnchorElement>,
    treatmentName: string,
    treatmentId: number
  ) => void,
  treatmentId: number,
  icon?: () => React.ReactElement
): {
  label: string;
  href: string;
  colour: ButtonColour;
  size: ButtonSize;
  onClick: (event: React.SyntheticEvent<HTMLAnchorElement>) => void;
  icon?: () => React.ReactElement;
} {
  return {
    label: substituteWithDictionary(cms['get-the-facts-link-template'], {
      treatment: treatmentName,
    }),
    href: generateUriFunction('treatment', {
      normalisedName: [treatmentNormalisedName],
    }),
    colour,
    size,
    icon,
    onClick: (event: React.SyntheticEvent<HTMLAnchorElement>) =>
      trackingFunction(event, treatmentName, treatmentId),
  };
}
